import { api } from './client'

/**
 * "Porta un amico" Ermes (`ReferralsController`, `/api/referrals`). Gli id sono id di contatto (scheda cliente).
 * Il modulo si scarica, oppure si carica sulla bozza Ermes dell'amico con `ermesUpload`.
 */
export interface ReferralItem {
  id: number
  friendCustomerId: number | null
  friendContractId: number | null
  friendCompanyName: string
  sheetNumber: number
  rowNumber: number
  bonusAmount: number
  bonusPaidAmount: number | null
  bonusPaidAt: string | null
  note: string | null
  createdAt: string
  friendContractReference: string | null
  friendContractSignedAt: string | null
  ermesCid: number | null
  ermesAttachmentId: string | null
  ermesUploadedAt: string | null
}

export interface ReferralList {
  referrerId: number
  items: ReferralItem[]
  bonusTotal: number
  bonusPaid: number
  hasBlankSheet: boolean
}

export interface CreateReferralInput {
  referrerId: number
  friendContactId?: number | undefined
  friendContractId?: number | undefined
  friendCompanyName?: string | undefined
  bonusAmount?: number | undefined
  note?: string | undefined
}

export const referralsApi = {
  list: (referrerId: number) => api<ReferralList>(`/api/referrals?referrerId=${referrerId}`),
  /** Contatto collegato a un cliente (i referral sono per contatto): 404 se nessuno. */
  contactForCustomer: (customerId: number) =>
    api<{ contactId: number }>(`/api/referrals/contact-for-customer?customerId=${customerId}`),
  create: (data: CreateReferralInput) =>
    api<{ id: number; sheetNumber: number; rowNumber: number }>('/api/referrals', { method: 'POST', body: JSON.stringify(data) }),
  markPaid: (id: number, amount?: number) =>
    api<void>(`/api/referrals/${id}/bonus-paid`, { method: 'POST', body: JSON.stringify({ amount: amount ?? null }) }),
  /** Carica il modulo come allegato della bozza Ermes `cid` dell'amico. */
  ermesUpload: (id: number, cid: number) =>
    api<{ ermesCid: number; ermesAttachmentId: string; ermesUploadedAt: string }>(
      `/api/referrals/${id}/ermes-upload`, { method: 'POST', body: JSON.stringify({ cid }) }),
  /** Link di download del foglio corrente (PDF compilato). */
  sheetUrl: (referrerId: number) => `/api/referrals/sheet?referrerId=${referrerId}`,
  /**
   * Carica (Admin, una volta sola) il PDF del modulo vergine di Ermes, base di tutti i fogli. `multipart/form-data`:
   * il browser imposta da solo il Content-Type, quindi non passa da `api()`.
   */
  uploadBlankSheet: async (file: File): Promise<void> => {
    const form = new FormData()
    form.append('file', file)
    const token = await fetch('/api/antiforgery/token', { credentials: 'include' })
      .then(r => (r.ok ? (r.json() as Promise<{ token: string }>) : null)).catch(() => null)
    const res = await fetch('/api/referrals/blank-sheet', {
      method: 'POST', credentials: 'include', headers: token ? { 'X-XSRF-TOKEN': token.token } : {}, body: form,
    })
    if (!res.ok) {
      const body = await res.json().catch(() => null) as { error?: string; title?: string } | null
      throw new Error(body?.error ?? body?.title ?? `HTTP ${res.status}`)
    }
  },
  searchContacts: (q: string) =>
    api<{ items: { id: number; firstName: string; lastName?: string | null; companyName?: string | null }[] }>(
      `/api/contacts?search=${encodeURIComponent(q)}&pageSize=10`),
  /** Clienti tra cui scegliere il segnalatore Ermes di una pratica (prima quelli con un contratto Ermes). */
  searchReferrers: (q: string) => api<{ items: ReferrerOption[] }>(`/api/referrals/referrers?q=${encodeURIComponent(q)}`),
}

export interface ReferrerOption {
  id: number
  name: string
  customerType: string
  city?: string | null
  /** Ha almeno un contratto Ermes nel CRM. */
  ermes: boolean
  /** Ha già un'anagrafica (contatto): altrimenti il caricamento la crea dal rappresentante legale. */
  contact: boolean
}
